// src/Context/CurrencyContext.jsx
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { getCurrencies, getCountries, updateCountryDiscount, getExchangeRates } from '../pages/api';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

// Default base currency (store all prices in USD in database)
const BASE_CURRENCY = 'USD';

export const CurrencyProvider = ({ children }) => {
  const [selectedCountry, setSelectedCountry] = useState(() => {
    const saved = localStorage.getItem("selectedCountry");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error parsing saved country:', e);
      }
    }
    // Default to United States
    return {
      id: 2,
      countryCode: "US",
      countryName: "United States",
      discountPercentage: 10,
      taxPercentage: 0,
      shippingBase: 0,
      currency: {
        code: "USD",
        symbol: "$",
        exchangeRate: 1.27
      }
    };
  });
  
  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [exchangeRates, setExchangeRates] = useState({});
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState(null);

  // Fetch all countries from backend
  const fetchCountries = useCallback(async () => {
    try {
      setCountriesLoading(true);
      setError(null);
      const response = await getCountries({ useCache: true });
      
      console.log('Fetched countries:', response);
      
      if (response && response.success === true && response.data && Array.isArray(response.data)) {
        // Use the data directly from your API response
        setCountries(response.data);
        localStorage.setItem('countries', JSON.stringify(response.data));
        
        // Update selected country if it exists in the list
        if (selectedCountry && selectedCountry.countryCode) {
          const matchingCountry = response.data.find(
            c => c.countryCode === selectedCountry.countryCode
          );
          
          if (matchingCountry) {
            setSelectedCountry(matchingCountry);
          }
        }
      } else {
        console.warn('No countries data received');
        setCountries([]);
      }
    } catch (error) {
      console.error('Failed to fetch countries:', error);
      setError('Failed to load countries');
      setCountries([]);
    } finally {
      setCountriesLoading(false);
    }
  }, [selectedCountry]);

  // Fetch all currencies from backend
  const fetchCurrencies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getCurrencies({ useCache: true });
      
      console.log('Fetched currencies:', response);
      
      if (response && response.success === true && response.data && Array.isArray(response.data)) {
        setCurrencies(response.data);
        
        // Transform to rates object
        const rates = {};
        response.data.forEach(currency => {
          if (currency.code && currency.exchangeRate) {
            rates[currency.code] = currency.exchangeRate;
          }
        });
        
        setExchangeRates(rates);
        localStorage.setItem('currencies', JSON.stringify(response.data));
        localStorage.setItem('exchangeRates', JSON.stringify(rates));
        setLastUpdated(new Date().toISOString());
      } else {
        console.warn('No currencies data received');
        setCurrencies([]);
        setExchangeRates({});
      }
    } catch (error) {
      console.error('Failed to fetch currencies:', error);
      setError('Failed to load currencies');
      setCurrencies([]);
      setExchangeRates({});
    } finally {
      setLoading(false);
    }
  }, []);

  // Load cached data on mount
  useEffect(() => {
    const loadCachedData = () => {
      try {
        // Load countries
        const cachedCountries = localStorage.getItem('countries');
        if (cachedCountries) {
          setCountries(JSON.parse(cachedCountries));
        }
        
        // Load currencies
        const cachedCurrencies = localStorage.getItem('currencies');
        if (cachedCurrencies) {
          setCurrencies(JSON.parse(cachedCurrencies));
        }
        
        // Load exchange rates
        const cachedRates = localStorage.getItem('exchangeRates');
        if (cachedRates) {
          setExchangeRates(JSON.parse(cachedRates));
        }
      } catch (e) {
        console.error('Error loading cached data:', e);
      }
    };
    
    loadCachedData();
    
    // Fetch fresh data
    fetchCurrencies();
    fetchCountries();
  }, [fetchCurrencies, fetchCountries]);

  // Update selected country with rate and discount
  const updateSelectedCountry = async (country) => {
    try {
      setSelectedCountry(country);
      localStorage.setItem("selectedCountry", JSON.stringify(country));
      
      // ✅ FIX: Do NOT call updateCountryDiscount – it's an admin-only endpoint.
      // The following block has been removed to prevent 403 errors.
      // If this country has a discount, we don't need to update it on the backend here.
      // if (country.discountPercentage > 0 && country.id) {
      //   try {
      //     await updateCountryDiscount(country.countryCode, { 
      //       discountPercentage: country.discountPercentage 
      //     });
      //   } catch (error) {
      //     console.error('Failed to update country discount:', error);
      //   }
      // }
      
      // Dispatch event so other components can react
      window.dispatchEvent(new CustomEvent('currencyChanged', { 
        detail: country 
      }));
    } catch (error) {
      console.error('Error updating selected country:', error);
    }
  };

  // Currency conversion function
  const convertPrice = useCallback((priceInUSD) => {
    if (!priceInUSD && priceInUSD !== 0) return 0;
    
    const numericPrice = typeof priceInUSD === 'string' 
      ? parseFloat(priceInUSD.replace(/[^\d.-]/g, '')) 
      : priceInUSD;
    
    if (isNaN(numericPrice)) return 0;
    
    // Get exchange rate from selected country's currency
    const rate = selectedCountry?.currency?.exchangeRate || 1;
    let converted = numericPrice * rate;
    
    // Apply country discount if any
    if (selectedCountry?.discountPercentage && selectedCountry.discountPercentage > 0) {
      converted = converted * (1 - selectedCountry.discountPercentage / 100);
    }
    
    // Round appropriately based on currency
    if (selectedCountry?.currency?.code === 'JPY' || selectedCountry?.currency?.code === 'VND') {
      converted = Math.round(converted);
    } else {
      converted = Math.round(converted * 100) / 100;
    }
    
    return converted;
  }, [selectedCountry]);

  // Format price with currency symbol
  const formatPrice = useCallback((priceInUSD, options = {}) => {
    const {
      showSymbol = true,
      showCode = false,
      decimals = 2,
      showDiscount = false
    } = options;
    
    if (!selectedCountry) return priceInUSD?.toString() || '0';
    
    let converted = convertPrice(priceInUSD);
    
    // Handle currencies with no decimals
    const currencyCode = selectedCountry?.currency?.code || 'USD';
    if (currencyCode === 'JPY' || currencyCode === 'VND') {
      converted = Math.round(converted);
    }
    
    // Format based on locale
    let formatted;
    try {
      switch (currencyCode) {
        case 'INR':
          formatted = converted.toLocaleString('en-IN', {
            minimumFractionDigits: currencyCode === 'JPY' ? 0 : decimals,
            maximumFractionDigits: currencyCode === 'JPY' ? 0 : decimals
          });
          break;
        case 'EUR':
          formatted = converted.toLocaleString('de-DE', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
          });
          break;
        case 'JPY':
          formatted = converted.toLocaleString('ja-JP', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          });
          break;
        default:
          formatted = converted.toLocaleString('en-US', {
            minimumFractionDigits: currencyCode === 'JPY' ? 0 : decimals,
            maximumFractionDigits: currencyCode === 'JPY' ? 0 : decimals
          });
      }
    } catch (e) {
      // Fallback formatting
      formatted = converted.toString();
    }
    
    const symbol = selectedCountry?.currency?.symbol || '$';
    const code = selectedCountry?.currency?.code || 'USD';
    
    let result = '';
    if (showSymbol && showCode) {
      result = `${symbol}${formatted} ${code}`;
    } else if (showSymbol) {
      result = `${symbol}${formatted}`;
    } else if (showCode) {
      result = `${formatted} ${code}`;
    } else {
      result = formatted;
    }
    
    // Add discount info if requested
    if (showDiscount && selectedCountry?.discountPercentage > 0) {
      const original = priceInUSD * (selectedCountry?.currency?.exchangeRate || 1);
      const originalFormatted = original.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      });
      result = `${result} (was ${symbol}${originalFormatted})`;
    }
    
    return result;
  }, [selectedCountry, convertPrice]);

  // Get country by code
  const getCountryByCode = useCallback((code) => {
    return countries.find(c => c.countryCode === code) || null;
  }, [countries]);

  // Get currency by code
  const getCurrencyByCode = useCallback((code) => {
    return currencies.find(c => c.code === code) || null;
  }, [currencies]);

  const refreshAll = useCallback(() => {
    fetchCurrencies();
    fetchCountries();
  }, [fetchCurrencies, fetchCountries]);

  return (
    <CurrencyContext.Provider value={{
      selectedCountry,
      setSelectedCountry: updateSelectedCountry,
      countries,
      currencies,
      currencyOpen,
      setCurrencyOpen,
      exchangeRates,
      loading,
      countriesLoading,
      lastUpdated,
      error,
      convertPrice,
      formatPrice,
      baseCurrency: BASE_CURRENCY,
      refreshRates: fetchCurrencies,
      refreshCountries: fetchCountries,
      refreshAll,
      getCountryByCode,
      getCurrencyByCode
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};